import { useRefreshAlgoliaData } from "entities/algolia/hooks/useRefreshAlgoliaData";
import { invalidateModuleListByTagIdQuery } from "entities/modules/queries/useModuleListByTagIdQuery";
import { deleteTag } from "entities/tags/api/deleteTag";
import { invalidateTagsListQuery } from "entities/tags/queries/useTagsListQuery";
import { Tag } from "entities/tags/types";
import React, { FC, useState, PropsWithChildren } from "react";
import { useClearRefinements } from "react-instantsearch";
import { useDrawerState } from "shared/hooks/useDrawerState";
import useToggle from "shared/hooks/useToggle";
import { createContext } from "shared/utils/context";
import { useSearchTagsData } from "../hooks/useSearchTagsData";
import { useTagTypeFilter } from "../hooks/useTagTypeFilter";
import { TagsPageType } from "./types";

const INITIAL_DELAY_MS = 2000;
const REFRESH_INTERVAL_MS = 5000;

const [Provider, useTagsPage] = createContext<TagsPageType>({
  name: "TagsPageContext",
  hookName: "useTagsPage",
  providerName: "TagsPageProvider",
});

const TagsPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const tagsDetailsDrawer = useDrawerState<Tag>();
  const { refreshAlgoliaData } = useRefreshAlgoliaData();
  const [openModal, setOpenModal] = useToggle(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  const { refine } = useClearRefinements();

  const {
    filteredValues: selectedMultipleTagTypes,
    filterData: tagTypeFilterData,
    setFilteredValues: setTagTypes,
    isDisabledButton: isDisabledTagTypesButton,
  } = useTagTypeFilter();

  const { searchData, isLoading, tableData } = useSearchTagsData({
    tagTypesIds: selectedMultipleTagTypes,
  });

  const onClearFilters = () => {
    setTagTypes([]);
    refine();
  };

  const onDeleteTag = async () => {
    try {
      if (selectedTag) {
        setLoadingDelete(true);

        await deleteTag(selectedTag.id);

        refreshAlgoliaData(INITIAL_DELAY_MS);
        refreshAlgoliaData(REFRESH_INTERVAL_MS);

        invalidateModuleListByTagIdQuery(selectedTag.id);
        tagsDetailsDrawer.setDrawerDetails(undefined);
        setSelectedTag(null);
        setOpenModal();
        invalidateTagsListQuery();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDelete(false);
    }
  };

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    tagsDetailsDrawer,
    loading: isLoading,
    loadingDelete,
    onClearFilters,
    tagTypeFilterData,
    searchData,
    openModal,
    setOpenModal,
    isDisabledTagTypesButton,
    selectedTag,
    setSelectedTag,
    onDeleteTag,
    tableData,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { TagsPageProvider, useTagsPage };
